import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Divider,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Card,
  CardContent,
  CardActions
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Map as MapIcon,
  Route as RouteIcon,
  CalendarMonth as CalendarIcon,
  Person as PersonIcon,
  DirectionsCar as CarIcon,
  Save as SaveIcon,
  LocationOn as LocationIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import InventoryIcon from '@mui/icons-material/Inventory';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ptBR } from 'date-fns/locale';
import { format } from 'date-fns';

import AuthLayout from '../components/AuthLayout';
import serviceRequestService from '../services/serviceRequest.service';
import userService from '../services/user.service';
import routeService, { Route } from '../services/route.service';
import assetService from '../services/asset.service';
import { isValidObjectId } from '../utils/mongoUtils';
import MapComponent from '../components/MapComponent';
import RouteMapComponent from '../components/RouteMapComponent';
import SimpleRouteMap from '../components/SimpleRouteMap';
// Importar mocks para quando a API não estiver disponível
import mockTechnicians from '../mocks/technicians.mock';
import mockServiceRequests from '../mocks/serviceRequests.mock';

// Definições de tipos
interface Location {
  lat: number;
  lng: number;
}

interface Asset {
  _id: string;
  name?: string;
  address?: any;
}

// Estendendo a interface ServiceRequest para ser compatível com ServiceRequestType
interface ServiceRequest {
  _id: string;
  number?: string;
  title?: string;
  description?: string;
  address?: string;
  location?: {
    lat: number;
    lng: number;
    address?: string;
    latitude?: number;
    longitude?: number;
  };
  asset?: Asset | string | {
    _id: string;
    name: string;
    category?: string;
  };
  client?: {
    _id?: string;
    name?: string;
    documentNumber?: string;
  };
  status?: string;
  priority?: string;
  type?: string;
}

interface Waypoint {
  id: string;
  title: string;
  address: string;
  location: Location;
  source: 'asset' | 'request';
  assetId?: string;
  assetName?: string;
}

interface OptimizedRoute {
  waypoints: Waypoint[];
  directions: any[];
  totalDistance: number;
  estimatedTime: number;
}

const RouteOptimization = () => {
  const navigate = useNavigate();
  
  // Estados para gerenciar os dados da rota
  const [serviceRequests, setServiceRequests] = useState<ServiceRequest[]>([]);
  const [selectedRequests, setSelectedRequests] = useState<string[]>([]);
  const [technicians, setTechnicians] = useState<any[]>([]);
  const [selectedTechnician, setSelectedTechnician] = useState<string>('');
  const [executionDate, setExecutionDate] = useState<Date>(new Date());
  const [startLocation, setStartLocation] = useState<Location>({ lat: -23.550520, lng: -46.633308 }); // São Paulo como padrão
  const [startLocationAddress, setStartLocationAddress] = useState<string>('Ponto de Partida');
  const [routeName, setRouteName] = useState<string>('');
  const [routeDescription, setRouteDescription] = useState<string>('');
  const [optimizedRoute, setOptimizedRoute] = useState<OptimizedRoute | null>(null);
  
  // Estados para controle de UI
  const [loading, setLoading] = useState<boolean>(false);
  const [calculating, setCalculating] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [singlePointMapOpen, setSinglePointMapOpen] = useState<boolean>(false);
  const [selectedPoint, setSelectedPoint] = useState<Waypoint | null>(null);
  const [startLocationDialogOpen, setStartLocationDialogOpen] = useState<boolean>(false);
  const [showRouteMap, setShowRouteMap] = useState<boolean>(false);
  const [savingRoute, setSavingRoute] = useState<boolean>(false);
  const [useSimpleMap, setUseSimpleMap] = useState<boolean>(false);
  
  // Carregar chamados de serviço disponíveis
  useEffect(() => {
    const fetchServiceRequests = async () => {
      setLoading(true);
      setError('');
      try {
        // Buscar chamados com status 'waiting' ou 'in_progress'
        console.log('Buscando chamados com status waiting ou in_progress...');
        
        // Verificar se há chamados no sistema
        try {
          const allResponse = await serviceRequestService.getServiceRequests({
            limit: 5
          });
          console.log('Verificando se há chamados no sistema:', allResponse);
        } catch (e) {
          console.error('Erro ao verificar chamados existentes:', e);
        }
        
        const response = await serviceRequestService.getServiceRequests({
          status: 'waiting,in_progress', // API espera uma string separada por vírgulas
          limit: 100,
          unassignedOnly: 'true' // Apenas chamados que não estão associados a rotas
        });
        
        console.log('Resposta da API de chamados com filtro de status:', response);
        
        // Verificar se a resposta tem o formato esperado com paginação
        if (response && Array.isArray(response.docs) && response.docs.length > 0) {
          console.log(`Encontrados ${response.docs.length} chamados disponíveis para roteirização`);
          // Converter ServiceRequestType[] para ServiceRequest[] com type assertion
          setServiceRequests(response.docs.map(req => ({
            ...req,
            _id: req._id || '', // Garantir que _id nunca seja undefined
            title: req.description || `Chamado ${req.number || ''}`,
            number: req.number || ''
          })) as ServiceRequest[]);
        } else if (response && Array.isArray(response) && response.length > 0) {
          // Caso a resposta seja um array direto (sem paginação)
          console.log(`Encontrados ${response.length} chamados disponíveis para roteirização (formato array)`);
          // Converter para ServiceRequest[] com type assertion
          setServiceRequests(response.map(req => ({
            ...req,
            _id: req._id || '', // Garantir que _id nunca seja undefined
            title: req.description || `Chamado ${req.number || ''}`,
            number: req.number || ''
          })) as ServiceRequest[]);
        } else {
          console.warn('Nenhum chamado encontrado ou formato de resposta inesperado:', response);
          setError('Nenhum chamado disponível para roteirização. Crie novos chamados com status "Aguardando" ou "Em Execução".');
          setServiceRequests([]);
        }
      } catch (error) {
        console.error('Erro ao carregar chamados:', error);
        setError('Erro ao carregar chamados. Verifique se o backend está em execução.');
        setServiceRequests([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchServiceRequests();
  }, []);
  
  // Carregar técnicos disponíveis
  useEffect(() => {
    const fetchTechnicians = async () => {
      try {
        // Usando o método específico para buscar técnicos
        const response = await userService.getAvailableTechnicians();
        
        // Verificar se a resposta tem o formato esperado
        if (Array.isArray(response) && response.length > 0) {
          setTechnicians(response);
        } else {
          console.warn('Formato de resposta inesperado para técnicos:', response);
          // Usar o mock de técnicos quando a API não retorna dados válidos
          console.log('Usando técnicos de mock');
          setTechnicians(mockTechnicians);
        }
      } catch (error) {
        console.error('Erro ao carregar técnicos:', error);
        console.log('Erro ao carregar técnicos, usando mock');
        // Usar o mock de técnicos em caso de erro na API
        setTechnicians(mockTechnicians);
      }
    };
    
    fetchTechnicians();
  }, []);
  
  // Função para selecionar/desselecionar chamados
  // @ts-ignore - Parâmetro requestId implicitamente tem tipo any
  const handleRequestSelection = (requestId) => {
    console.log('Selecionando/desselecionando chamado:', requestId);
    
    // Verificar se o requestId é um objeto ou um ID
    const idToUse = typeof requestId === 'object' && (requestId as any)._id ? (requestId as any)._id : requestId;
    console.log('ID a ser usado:', idToUse);
    
    setSelectedRequests(prev => {
      // Verificar se o ID já está na lista (considerando tanto objetos quanto strings)
      const isAlreadySelected = prev.some(item => {
        const itemId = typeof item === 'object' && (item as any)._id ? (item as any)._id : item;
        return itemId === idToUse;
      });
      
      if (isAlreadySelected) {
        console.log('Removendo chamado da seleção:', idToUse);
        return prev.filter(item => {
          // @ts-ignore - Propriedade _id não existe no tipo never
          const itemId = typeof item === 'object' && (item as any)._id ? (item as any)._id : item;
          return itemId !== idToUse;
        });
      } else {
        console.log('Adicionando chamado à seleção:', idToUse);
        return [...prev, idToUse];
      }
    });
  };
  
  // Função para calcular a rota otimizada
  const calculateOptimizedRoute = async () => {
    if (!selectedTechnician) {
      setError('Selecione um técnico para a rota.');
      return;
    }
    
    if (!Array.isArray(selectedRequests) || selectedRequests.length < 2) {
      setError('Selecione pelo menos 2 chamados para calcular a rota.');
      return;
    }
    
    if (!startLocation.lat || !startLocation.lng) {
      setError('Defina o ponto de partida para a rota.');
      return;
    }
    
    setCalculating(true);
    setError('');
    
    try {
      // Preparar os dados para o cálculo da rota
      const waypoints = [];
      const invalidRequests = [];
      
      console.log('Chamados selecionados para cálculo:', selectedRequests);
      console.log('Total de chamados disponíveis:', serviceRequests.length);
      
      // Adicionar ponto de partida como primeiro waypoint
      waypoints.push({
        id: 'start',
        title: 'Ponto de Partida',
        address: startLocationAddress || 'Ponto de Partida',
        location: {
          // @ts-ignore - Argumento do tipo number não é atribuível ao parâmetro do tipo string
          lat: parseFloat(startLocation.lat as any) || 0,
          // @ts-ignore - Argumento do tipo number não é atribuível ao parâmetro do tipo string
          lng: parseFloat(startLocation.lng as any) || 0
        }
      });
      
      // Adicionar waypoints para cada chamado selecionado
      for (const requestId of selectedRequests) {
        // Extrair o ID do chamado (caso seja um objeto)
        // @ts-ignore - Propriedade _id não existe no tipo never
        const idToFind = typeof requestId === 'object' && (requestId as any)._id ? (requestId as any)._id : requestId;
        console.log(`Buscando chamado com ID: ${idToFind}`);
        
        // Encontrar o chamado completo com base no ID
        const request = serviceRequests.find(req => {
          // @ts-ignore - Propriedade id não existe no tipo ServiceRequest
          const reqId = typeof req === 'object' && (req as any)._id ? (req as any)._id : (typeof req === 'object' && (req as any).id ? (req as any).id : req);
          return reqId === idToFind;
        });
        
        if (!request) {
          console.warn(`Chamado com ID ${idToFind} não foi encontrado na lista de chamados.`);
          invalidRequests.push({ id: idToFind, reason: 'não encontrado na lista de chamados' });
          continue;
        }
        
        let locationFromAsset = null;
        let addressFromAsset = null;
        
        // Verificar se o chamado tem um ativo associado e tentar obter as coordenadas do ativo
        if (request.asset && typeof request.asset === 'object' && request.asset._id) {
          try {
            const assetId = request.asset._id;
            // Verificar se o ID do ativo é um ObjectId válido
            if (!isValidObjectId(assetId)) {
              console.warn(`ID de ativo inválido: ${assetId}. Não é um ObjectId válido do MongoDB.`);
              // Continuar com as coordenadas do chamado, se disponíveis
              continue;
            }
            
            console.log(`Buscando coordenadas do ativo ${assetId} associado ao chamado ${idToFind}`);
            const assetData = await assetService.getAssetById(assetId);
            
            if (assetData && assetData.asset && assetData.asset.address && 
                assetData.asset.address.coordinates && 
                assetData.asset.address.coordinates.lat && 
                assetData.asset.address.coordinates.lng) {
              
              locationFromAsset = {
                // @ts-ignore - Argumento do tipo number não é atribuível ao parâmetro do tipo string
                lat: parseFloat(assetData.asset.address.coordinates.lat as any),
                // @ts-ignore - Argumento do tipo number não é atribuível ao parâmetro do tipo string
                lng: parseFloat(assetData.asset.address.coordinates.lng as any)
              };
              
              // Montar o endereço formatado do ativo
              const addr = assetData.asset.address;
              const addressParts = [];
              
              if (addr.street) {
                let streetPart = addr.street;
                if (addr.number) streetPart += `, ${addr.number}`;
                if (addr.complement) streetPart += ` - ${addr.complement}`;
                addressParts.push(streetPart);
              }
              
              if (addr.neighborhood) addressParts.push(addr.neighborhood);
              
              if (addr.city) {
                let cityPart = addr.city;
                if (addr.state) cityPart += `/${addr.state}`;
                addressParts.push(cityPart);
              }
              
              if (addr.zipCode) addressParts.push(addr.zipCode);
              
              addressFromAsset = addressParts.join(', ');
              
              console.log(`Coordenadas do ativo encontradas: ${JSON.stringify(locationFromAsset)}`);
              console.log(`Endereço do ativo: ${addressFromAsset}`);
            }
          } catch (assetError) {
            console.error(`Erro ao buscar coordenadas do ativo ${request.asset._id}:`, assetError);
          }
        } else if (request.asset && typeof request.asset === 'string') {
          try {
            const assetId = request.asset;
            // Verificar se o ID do ativo é um ObjectId válido
            if (!isValidObjectId(assetId)) {
              console.warn(`ID de ativo inválido: ${assetId}. Não é um ObjectId válido do MongoDB.`);
              // Continuar com as coordenadas do chamado, se disponíveis
              continue;
            }
            
            console.log(`Buscando coordenadas do ativo ${assetId} associado ao chamado ${idToFind}`);
            const assetData = await assetService.getAssetById(assetId);
            
            if (assetData && assetData.asset && assetData.asset.address && 
                assetData.asset.address.coordinates && 
                assetData.asset.address.coordinates.lat && 
                assetData.asset.address.coordinates.lng) {
              
              locationFromAsset = {
                // @ts-ignore - Argumento do tipo number não é atribuível ao parâmetro do tipo string
                lat: parseFloat(assetData.asset.address.coordinates.lat as any),
                // @ts-ignore - Argumento do tipo number não é atribuível ao parâmetro do tipo string
                lng: parseFloat(assetData.asset.address.coordinates.lng as any)
              };
              
              // Montar o endereço formatado do ativo
              const addr = assetData.asset.address;
              const addressParts = [];
              
              if (addr.street) {
                let streetPart = addr.street;
                if (addr.number) streetPart += `, ${addr.number}`;
                if (addr.complement) streetPart += ` - ${addr.complement}`;
                addressParts.push(streetPart);
              }
              
              if (addr.neighborhood) addressParts.push(addr.neighborhood);
              
              if (addr.city) {
                let cityPart = addr.city;
                if (addr.state) cityPart += `/${addr.state}`;
                addressParts.push(cityPart);
              }
              
              if (addr.zipCode) addressParts.push(addr.zipCode);
              
              addressFromAsset = addressParts.join(', ');
              
              console.log(`Coordenadas do ativo encontradas: ${JSON.stringify(locationFromAsset)}`);
              console.log(`Endereço do ativo: ${addressFromAsset}`);
            }
          } catch (assetError) {
            console.error(`Erro ao buscar coordenadas do ativo ${request.asset}:`, assetError);
          }
        }
        
        // Verificar se o chamado tem localização válida (própria ou do ativo)
        console.log(`Verificando localização do chamado ${idToFind}:`, request.location);
        
        // Verificar se o chamado tem campos latitude/longitude ou lat/lng
        const hasLatLng = request.location && request.location.lat && request.location.lng;
        const hasLatitudeLongitude = request.location && 
                                  (request.location.latitude !== undefined && request.location.latitude !== null) && 
                                  (request.location.longitude !== undefined && request.location.longitude !== null);
        
        // Verificar localização do ativo
        const hasAssetLocation = locationFromAsset && locationFromAsset.lat && locationFromAsset.lng;
        
        console.log(`Chamado ${idToFind} - hasLatLng: ${hasLatLng}, hasLatitudeLongitude: ${hasLatitudeLongitude}, hasAssetLocation: ${hasAssetLocation}`);
        
        if (!hasLatLng && !hasLatitudeLongitude && !hasAssetLocation) {
          console.warn(`Chamado ${idToFind} não tem localização válida e seu ativo também não.`);
          invalidRequests.push({ id: idToFind, reason: 'sem localização válida no chamado ou no ativo' });
          continue;
        }
        
        // Extrair o ID definitivo do chamado
        // @ts-ignore - Propriedade id não existe no tipo ServiceRequest
        const requestIdToUse = (request as any)._id || (request as any).id;
        if (!requestIdToUse) {
          console.warn(`Chamado não possui ID válido:`, request);
          invalidRequests.push({ id: String(idToFind), reason: 'ID inválido no objeto do chamado' });
          continue;
        }
        
        // Priorizar localização do ativo se disponível
        let locationToUse;
        
        if (hasAssetLocation) {
          // Usar localização do ativo
          locationToUse = locationFromAsset;
        } else if (hasLatLng) {
          // Usar lat/lng do chamado
          locationToUse = {
            // @ts-ignore - request.location possivelmente undefined
            lat: parseFloat(request.location.lat) || 0,
            // @ts-ignore - request.location possivelmente undefined
            lng: parseFloat(request.location.lng) || 0
          };
        } else if (hasLatitudeLongitude) {
          // Usar latitude/longitude do chamado
          locationToUse = {
            // @ts-ignore - request.location possivelmente undefined
            lat: parseFloat(String(request.location.latitude)) || 0,
            // @ts-ignore - request.location possivelmente undefined
            lng: parseFloat(String(request.location.longitude)) || 0
          };
        } else {
          // Caso improvável, mas para garantir que sempre temos um objeto válido
          console.error(`Chamado ${idToFind} passou na validação de localização mas não tem coordenadas válidas.`);
          locationToUse = { lat: 0, lng: 0 };
        }
        
        console.log(`Localização a ser usada para o chamado ${idToFind}:`, locationToUse);
        
        const addressToUse = hasAssetLocation && addressFromAsset ? 
          addressFromAsset : (request.address || 'Endereço não disponível');
        
        // Adicionar waypoint com dados validados
        const waypointData: Waypoint = {
          id: requestIdToUse,
          title: request.title || `Chamado ${request.number || ''}`,
          address: addressToUse,
          // @ts-ignore - Tipo { lat: number; lng: number; } | null não é atribuível ao tipo Location
          location: locationToUse,
          source: hasAssetLocation ? 'asset' : 'request' // Indicar a fonte da localização
        };
        
        // Adicionar informações do ativo se disponíveis
        if (hasAssetLocation) {
          waypointData.assetId = typeof request.asset === 'object' ? request.asset._id : request.asset;
          waypointData.assetName = request.asset && typeof request.asset === 'object' && request.asset.name ? request.asset.name : 'Ativo associado';
        }
        
        waypoints.push(waypointData);
        
        console.log(`Waypoint adicionado para chamado ${requestIdToUse} usando localização ${hasAssetLocation ? 'do ativo' : 'do chamado'}`);
      }
      
      // Verificar se temos waypoints suficientes (além do ponto de partida)
      if (waypoints.length <= 1) {
        console.error('Chamados inválidos:', invalidRequests);
        throw new Error('Não há chamados com localização válida para calcular a rota.');
      }
      
      console.log(`Total de ${waypoints.length} waypoints válidos para otimização:`, waypoints);
      console.log(`Total de ${invalidRequests.length} chamados inválidos:`, invalidRequests);
      
      // Chamar a API para calcular a rota otimizada
      const response = await routeService.calculateOptimizedRoute(waypoints);
      
      console.log('Resposta completa da API de otimização:', response);
      
      // Verificar se a resposta foi bem-sucedida
      if (!response.success || !response.data) {
        throw new Error(response.error || 'A API retornou uma resposta inválida.');
      }
      
      // Verificar se a resposta contém waypoints válidos
      if (!response.data.waypoints || response.data.waypoints.length <= 1) {
        throw new Error('A API retornou uma rota sem waypoints válidos.');
      }
      
      // Atualizar o estado com a rota otimizada
      setOptimizedRoute(response.data);
      
      // Mostrar mensagem de sucesso
      setSuccess('Rota otimizada calculada com sucesso!');
    } catch (error: any) {
      console.error('Erro ao calcular rota otimizada:', error);
      if (error.response && error.response.data && error.response.data.message) {
        setError(`Erro ao calcular rota: ${error.response.data.message}`);
      } else if (error.message) {
        setError(`Erro ao calcular rota: ${error.message}`);
      } else {
        setError('Não foi possível calcular a rota otimizada. Verifique os dados e tente novamente.');
      }
    } finally {
      setCalculating(false);
    }
  };
  
  // Função para salvar a rota otimizada
  const handleSaveRoute = async () => {
    if (!optimizedRoute || !routeName.trim() || !selectedTechnician) {
      setError('Preencha todos os campos obrigatórios: nome da rota e técnico responsável');
      return;
    }

    setSavingRoute(true);
    setError('');
    setSuccess('');

    try {
      console.log('Preparando dados para salvar rota...');
      
      // Extrair IDs dos chamados de serviço dos waypoints
      const serviceRequestIds = optimizedRoute.waypoints
        .filter(wp => wp.id !== 'start') // Remover o ponto de partida
        .map(wp => wp.id);
      
      console.log('IDs dos chamados a serem salvos:', serviceRequestIds);
      
      // Verificar se o ID do técnico é válido
      const isValidTechnicianId = isValidObjectId(selectedTechnician);
      console.log(`ID do técnico selecionado: ${selectedTechnician}, válido: ${isValidTechnicianId}`);
      
      // Encontrar o técnico selecionado para obter o ID correto
      const selectedTech = technicians.find(tech => tech._id === selectedTechnician || tech.id === selectedTechnician);
      console.log('Técnico selecionado:', selectedTech);
      
      // Usar o ID correto do técnico ou null se não for válido
      const technicianId = isValidTechnicianId ? selectedTechnician : (selectedTech?._id || null);
      
      // Preparar os dados da rota para salvar conforme o modelo do backend
      const routeData: Route = {
        name: routeName.trim(),
        description: routeDescription.trim(),
        technician: technicianId, // Usar o ID válido ou null
        date: executionDate, // O backend espera um campo 'date', não 'executionDate'
        serviceRequests: serviceRequestIds, // O backend espera um array de IDs, não waypoints
        status: 'pending' as const, // Usar type assertion para garantir que o tipo seja correto
        optimized: true,
        totalDistance: parseFloat(String(optimizedRoute.totalDistance)) || 0,
        estimatedDuration: parseFloat(String(optimizedRoute.estimatedTime)) || 0
      };
      
      console.log('Dados da rota a serem enviados:', routeData);

      // Chamar o serviço para criar a rota
      const response = await routeService.createRoute(routeData);
      console.log('Resposta do servidor:', response);

      // Verificar se a resposta contém um erro (pode vir do formato { error, data } ou do axios)
      if ('error' in response && response.error) {
        setError(`Erro ao salvar rota: ${response.error}`);
      } else {
        setSuccess('Rota salva com sucesso!');
        // Redirecionar para a lista de rotas após um breve delay
        setTimeout(() => {
          navigate('/routes');
        }, 2000);
      }
    } catch (error: any) {
      console.error('Erro ao salvar rota:', error);
      setError(`Erro ao salvar rota: ${error?.message || 'Erro desconhecido'}`);
    } finally {
      setSavingRoute(false);
    }
  };

  // Função para visualizar um ponto no mapa
  const handleViewPointOnMap = (request: ServiceRequest) => {
    // Verificar se o chamado tem coordenadas próprias
    const hasRequestCoordinates = request.location && 
                                 request.location.lat && 
                                 request.location.lng;
    
    // Se o chamado tem coordenadas, usar essas coordenadas
    if (hasRequestCoordinates && request.location) {
      setSelectedPoint({
        id: request._id,
        title: request.title || `Chamado #${request.number || 'N/A'}`,
        address: request.address || 'Endereço não informado',
        location: {
          lat: request.location.lat,
          lng: request.location.lng
        },
        source: 'request'
      });
      setSinglePointMapOpen(true);
      return;
    }
    
    // Se o chamado não tem coordenadas, mas tem um ativo associado
    const assetId = typeof request.asset === 'object' ? request.asset?._id : request.asset;
    const assetName = typeof request.asset === 'object' ? request.asset?.name : '';
    
    if (assetId) {
      // Buscar as coordenadas do ativo
      // Para simplificar, vamos usar coordenadas de exemplo
      // Em um caso real, você buscaria as coordenadas do ativo na API
      setSelectedPoint({
        id: request._id,
        title: request.title || `Chamado #${request.number || 'N/A'}`,
        address: request.address || 'Endereço não informado',
        location: {
          lat: -23.550520, // Coordenadas de exemplo
          lng: -46.633308  // Em um caso real, buscar do ativo
        },
        source: 'asset',
        assetId: assetId as string,
        assetName: assetName
      });
      setSinglePointMapOpen(true);
    } else {
      // Se não tem coordenadas nem ativo, mostrar um erro
      setError('Este chamado não possui coordenadas para visualização no mapa.');
    }
  };

  // Função para visualizar a rota no mapa
  const visualizeRoute = async () => {
    if (!optimizedRoute || !optimizedRoute.waypoints || !Array.isArray(optimizedRoute.waypoints) || optimizedRoute.waypoints.length === 0) {
      setError('Não há waypoints válidos na rota otimizada');
      return;
    }
    
    setError('');
    
    try {
      // Extrair IDs válidos dos waypoints, excluindo o ponto de partida (id='start') e filtrando nulos
      const serviceRequestIds = optimizedRoute.waypoints
        .filter(waypoint => waypoint && waypoint.id && waypoint.id !== 'start')
        .map(waypoint => waypoint.id);
      
      console.log('IDs dos chamados a serem salvos (dos waypoints otimizados):', serviceRequestIds);
      
      // Verificar se temos IDs válidos
      if (!serviceRequestIds.length) {
        throw new Error('Não há chamados válidos para criar a rota');
      }
      
      // Formatar a data de execução corretamente
      const formattedDate = new Date(executionDate);
      
      // Processar waypoints com validação para envio
      const processedWaypoints = optimizedRoute.waypoints
        .filter(wp => wp && wp.location) // Filtrar waypoints nulos ou sem localização
        .map(wp => ({
          id: wp.id,
          title: wp.title || 'Sem título',
          address: wp.address || 'Sem endereço',
          location: {
            // @ts-ignore - Argumento do tipo number não é atribuível ao parâmetro do tipo string
            lat: parseFloat(wp.location.lat) || 0,
            // @ts-ignore - Argumento do tipo number não é atribuível ao parâmetro do tipo string
            lng: parseFloat(wp.location.lng) || 0
          }
        }));
      
      // Dados da rota para salvar
      const routeData = {
        name: routeName,
        description: routeDescription,
        technicianId: selectedTechnician,
        // @ts-ignore - Type 'Date' is not assignable to type 'string'
        executionDate: formattedDate,
        startLocation: {
          // @ts-ignore - Argumento do tipo number não é atribuível ao parâmetro do tipo string
          lat: parseFloat(startLocation.lat as any) || 0,
          // @ts-ignore - Argumento do tipo number não é atribuível ao parâmetro do tipo string
          lng: parseFloat(startLocation.lng as any) || 0
        },
        serviceRequests: serviceRequestIds,
        optimizedWaypoints: processedWaypoints,
        // @ts-ignore - Argumento do tipo number não é atribuível ao parâmetro do tipo string
        totalDistance: parseFloat(optimizedRoute.totalDistance) || 0,
        // @ts-ignore - Argumento do tipo number | "0" não é atribuível ao parâmetro do tipo string
        estimatedTime: parseFloat(optimizedRoute.estimatedTime || '0'),
        directions: optimizedRoute.directions || [],
        status: 'scheduled'
      };
      
      // Chamar a API para salvar a rota
      // @ts-ignore - Incompatibilidade de tipos entre Date e string
      const response = await routeService.createRoute(routeData);
      
      setSuccess('Rota salva com sucesso!');
      
      // Redirecionar para a página de detalhes da rota após um breve delay
      setTimeout(() => {
        if (response.data && response.data._id) {
          navigate(`/routes/${response.data._id}`);
        } else {
          navigate('/routes'); // Fallback para a lista de rotas se não tiver ID
        }
      }, 2000);
    } catch (error: any) {
      console.error('Erro ao salvar rota:', error);
      if (error instanceof Error) {
        setError(`Erro ao salvar rota: ${error.message}`);
      } else if (error.response && error.response.data && error.response.data.message) {
        setError(`Erro ao salvar rota: ${error.response.data.message}`);
      } else {
        setError('Não foi possível salvar a rota. Verifique os dados e tente novamente.');
      }
    } finally {
      setSavingRoute(false);
    }
  };
  
  // Renderizar a lista de chamados disponíveis
  const renderServiceRequests = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      );
    }
    
    // Verificar se serviceRequests é um array válido
    if (!serviceRequests || !Array.isArray(serviceRequests)) {
      return (
        <Alert severity="warning">
          Erro ao carregar chamados. Tente novamente mais tarde.
        </Alert>
      );
    }
    
    if (serviceRequests.length === 0) {
      return (
        <Alert severity="info">
          Não há chamados disponíveis para roteirização.
        </Alert>
      );
    }
    
    return (
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox"></TableCell>
              <TableCell>Número</TableCell>
              <TableCell>Cliente</TableCell>
              <TableCell>Título</TableCell>
              <TableCell>Endereço</TableCell>
              <TableCell>Localização</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.isArray(serviceRequests) && serviceRequests.map((request) => {
              // Verificar se o chamado tem coordenadas próprias
              const hasRequestCoordinates = request.location && 
                                           request.location.lat && 
                                           request.location.lng;
              
              // Verificar se o chamado tem um ativo associado
              const hasAsset = request.asset && 
                             (typeof request.asset === 'string' || 
                              (typeof request.asset === 'object' && request.asset._id));
              
              return (
                <TableRow 
                  key={request._id}
                  selected={selectedRequests.includes(request._id)}
                  hover
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedRequests.includes(request._id)}
                      onChange={() => handleRequestSelection(request._id)}
                    />
                  </TableCell>
                  <TableCell>{request.number}</TableCell>
                  <TableCell>{request.client?.name || 'N/A'}</TableCell>
                  <TableCell>
                    {request.title}
                    {hasAsset && (
                      <Tooltip title="Chamado associado a um ativo">
                        <InventoryIcon 
                          fontSize="small" 
                          color="primary" 
                          sx={{ ml: 1, verticalAlign: 'middle', fontSize: '0.9rem' }} 
                        />
                      </Tooltip>
                    )}
                  </TableCell>
                  {/* @ts-ignore - Propriedade address não existe no tipo Location */}
                  <TableCell>{request.location?.address || 'Endereço não informado'}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {hasRequestCoordinates ? (
                        <Tooltip title="Chamado com coordenadas próprias">
                          <Chip 
                            icon={<LocationIcon fontSize="small" />}
                            label="Coordenadas" 
                            color="success"
                            size="small"
                          />
                        </Tooltip>
                      ) : hasAsset ? (
                        <Tooltip title="Coordenadas disponíveis via ativo associado">
                          <Chip 
                            icon={<InventoryIcon fontSize="small" />}
                            label="Via Ativo" 
                            color="info"
                            size="small"
                          />
                        </Tooltip>
                      ) : (
                        <Tooltip title="Sem coordenadas disponíveis">
                          <Chip 
                            icon={<LocationIcon fontSize="small" />}
                            label="Sem coordenadas" 
                            color="error"
                            size="small"
                          />
                        </Tooltip>
                      )}
                      
                      {(hasRequestCoordinates || hasAsset) && (
                        <Tooltip title="Visualizar no mapa">
                          <IconButton 
                            size="small" 
                            color="primary" 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewPointOnMap(request);
                            }}
                          >
                            <MapIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={(request as any).status === 'waiting' ? 'Aguardando' : 'Em Execução'}
                      color={(request as any).status === 'waiting' ? 'warning' : 'info'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Ver no mapa">
                      <IconButton 
                        size="small" 
                        onClick={() => {
                          // Implementar visualização no mapa
                        }}
                        disabled={!hasRequestCoordinates}
                      >
                        <MapIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Ver detalhes">
                      <IconButton 
                        size="small" 
                        onClick={() => {
                          // Verificar se o ID é válido antes de navegar
                          if (request && request._id && isValidObjectId(request._id)) {
                            navigate(`/service-requests/${request._id}`);
                          } else {
                            setError(`ID inválido: ${request?._id} não é um ObjectId válido do MongoDB`);
                          }
                        }}
                      >
                        <RouteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };
  
  // Renderizar detalhes da rota otimizada
  const renderOptimizedRoute = () => {
    if (!optimizedRoute) return null;
    
    return (
      <Paper elevation={1} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Rota Otimizada
        </Typography>
        
        <Box sx={{ mb: 3 }}>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Distância Total
                  </Typography>
                  <Typography variant="h6">
                    {optimizedRoute.totalDistance ? `${(optimizedRoute.totalDistance / 1000).toFixed(1)} km` : 'N/A'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Tempo Estimado
                  </Typography>
                  <Typography variant="h6">
                    {optimizedRoute.estimatedTime ? `${Math.round(optimizedRoute.estimatedTime / 60)} min` : 'N/A'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Pontos da Rota
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    {(() => {
                      // Calcular estatísticas de origem das coordenadas
                      const totalWaypoints = optimizedRoute.waypoints.length - 1; // Excluir ponto de partida
                      const waypointsFromAssets = optimizedRoute.waypoints.filter(wp => wp.source === 'asset').length;
                      const waypointsFromRequests = totalWaypoints - waypointsFromAssets;
                      
                      return (
                        <>
                          <Tooltip title="Pontos usando coordenadas do ativo">
                            <Chip 
                              icon={<InventoryIcon fontSize="small" />}
                              label={waypointsFromAssets} 
                              color="info"
                              size="small"
                              variant="outlined"
                            />
                          </Tooltip>
                          <Tooltip title="Pontos usando coordenadas do chamado">
                            <Chip 
                              icon={<LocationIcon fontSize="small" />}
                              label={waypointsFromRequests} 
                              color="primary"
                              size="small"
                              variant="outlined"
                            />
                          </Tooltip>
                        </>
                      );
                    })()} 
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
          
          <Typography variant="subtitle1" gutterBottom>
            Sequência de Visitas
          </Typography>
          
          <List>
            {optimizedRoute.waypoints.map((waypoint, index) => {
              // Verificar se a localização vem do ativo
              const isFromAsset = waypoint.source === 'asset';
              
              return (
                <ListItem key={index} divider={index < optimizedRoute.waypoints.length - 1}>
                  <ListItemIcon>
                    {index === 0 ? (
                      <Chip 
                        label="Partida" 
                        color="success" 
                        size="small" 
                        icon={<CarIcon />} 
                      />
                    ) : (
                      <Chip 
                        label={`Parada ${index}`} 
                        color="primary" 
                        size="small" 
                        icon={isFromAsset ? <InventoryIcon /> : <LocationIcon />} 
                      />
                    )}
                  </ListItemIcon>
                  <ListItemText 
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body1" component="span">
                          {waypoint.title}
                        </Typography>
                        {index > 0 && isFromAsset && (
                          <Tooltip title="Localização obtida do ativo">
                            <Chip 
                              label="Via Ativo" 
                              size="small" 
                              color="info"
                              variant="outlined"
                              sx={{ ml: 1, height: 20, fontSize: '0.6rem' }}
                            />
                          </Tooltip>
                        )}
                      </Box>
                    } 
                    secondary={
                      <Typography variant="body2" color="text.secondary">
                        {waypoint.address || 'Endereço não informado'}
                        {waypoint.location && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            Lat: {waypoint.location.lat.toFixed(6)}, Lng: {waypoint.location.lng.toFixed(6)}
                          </Typography>
                        )}
                      </Typography>
                    } 
                  />
                </ListItem>
              );
            })}
          </List>
        </Box>
        
        <Box sx={{ width: '100%', mb: 2, textAlign: 'center', display: 'flex', justifyContent: 'center', gap: 2 }}>
          <Button 
            variant="contained" 
            startIcon={<MapIcon />}
            onClick={() => setShowRouteMap(true)}
            sx={{ mt: 2 }}
          >
            Ver Mapa da Rota
          </Button>
        </Box>
      </Paper>
    );
  };
  return (
    <AuthLayout title="Otimização de Rotas">
      <Box sx={{ width: '100%' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}
        
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" gutterBottom>
            Otimização de Rotas
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Selecione os chamados, o técnico responsável e a data de execução para calcular a rota mais eficiente.
          </Typography>
          
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel id="technician-select-label">Técnico Responsável</InputLabel>
                <Select
                  labelId="technician-select-label"
                  id="technician-select"
                  value={selectedTechnician}
                  label="Técnico Responsável"
                  onChange={(e) => setSelectedTechnician(e.target.value)}
                >
                  <MenuItem value=""><em>Selecione um técnico</em></MenuItem>
                  {Array.isArray(technicians) && technicians.map((tech) => (
                    <MenuItem key={tech._id || tech.id || `tech-${Math.random()}`} value={tech._id || tech.id}>
                      {tech.displayName || tech.name || tech.email || 'Técnico sem nome'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
                <DatePicker
                  label="Data de Execução"
                  value={executionDate}
                  onChange={(newValue) => setExecutionDate(newValue as any)}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </LocalizationProvider>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nome da Rota"
                value={routeName}
                onChange={(e) => setRouteName(e.target.value)}
                placeholder="Ex: Rota Norte - 29/05/2025"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Descrição (opcional)"
                value={routeDescription}
                onChange={(e) => setRouteDescription(e.target.value)}
                placeholder="Informações adicionais sobre esta rota"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" sx={{ mr: 2 }}>
                  Ponto de Partida
                </Typography>
                <Button 
                  variant="outlined" 
                  size="small"
                  startIcon={<MapIcon />}
                  onClick={() => setStartLocationDialogOpen(true)}
                >
                  Definir no Mapa
                </Button>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Coordenadas: {startLocation.lat.toFixed(6)}, {startLocation.lng.toFixed(6)}
              </Typography>
            </Grid>
          </Grid>
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<RouteIcon />}
              onClick={calculateOptimizedRoute}
              disabled={calculating || !Array.isArray(selectedRequests) || selectedRequests.length < 2 || !selectedTechnician}
              sx={{ mr: 1 }}
            >
              {calculating ? <CircularProgress size={24} /> : 'Calcular Rota'}
            </Button>
            
            <Button
              variant="contained"
              color="success"
              startIcon={savingRoute ? null : <SaveIcon />}
              onClick={handleSaveRoute}
              disabled={!optimizedRoute || savingRoute || !routeName.trim() || !selectedTechnician}
            >
              {savingRoute ? <CircularProgress size={24} /> : 'Salvar Rota'}
            </Button>
          </Box>
        </Paper>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={optimizedRoute ? 7 : 12}>
            <Paper elevation={1} sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Chamados Disponíveis
                </Typography>
                <Box>
                  <Chip 
                    label={`${Array.isArray(selectedRequests) ? selectedRequests.length : 0} selecionados`} 
                    color="primary" 
                    sx={{ mr: 1 }}
                  />
                  <Button 
                    variant="outlined" 
                    size="small"
                    onClick={() => setSelectedRequests([])}
                    disabled={selectedRequests.length === 0}
                  >
                    Limpar
                  </Button>
                </Box>
              </Box>
              
              {renderServiceRequests()}
            </Paper>
          </Grid>
          
          {optimizedRoute && (
            <Grid item xs={12} md={5}>
              {renderOptimizedRoute()}
            </Grid>
          )}
        </Grid>
        
        {/* Removido o diálogo de visualização da rota otimizada que não estava sendo usado */}
        
        {/* Diálogo para visualizar um ponto específico no mapa */}
        <Dialog
          open={singlePointMapOpen}
          onClose={() => setSinglePointMapOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">Localização do Chamado</Typography>
              {selectedPoint && selectedPoint.source === 'asset' && (
                <Chip 
                  icon={<InventoryIcon fontSize="small" />}
                  label="Localização do Ativo" 
                  color="info"
                  size="small"
                />
              )}
            </Box>
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={8}>
                <Box sx={{ height: '50vh', width: '100%' }}>
                  {selectedPoint && selectedPoint.location && (
                    /* @ts-ignore - Propriedades não existem no tipo IntrinsicAttributes */
                    <SimpleRouteMap 
                      waypoints={[selectedPoint]}
                      center={selectedPoint.location}
                      zoom={15}
                    />
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                {selectedPoint && (
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        {selectedPoint.title}
                      </Typography>
                      
                      {selectedPoint.source === 'asset' && (
                        <Box sx={{ 
                          mb: 2, 
                          p: 1, 
                          bgcolor: 'info.light', 
                          borderRadius: 1,
                          color: 'info.contrastText'
                        }}>
                          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <InventoryIcon fontSize="small" />
                            Coordenadas obtidas do ativo associado
                          </Typography>
                          {selectedPoint.assetName && (
                            <Typography variant="caption" display="block">
                              Ativo: {selectedPoint.assetName}
                            </Typography>
                          )}
                        </Box>
                      )}
                      
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Endereço
                      </Typography>
                      <Typography variant="body2" paragraph>
                        {selectedPoint.address || 'Endereço não informado'}
                      </Typography>
                      
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Coordenadas
                      </Typography>
                      <Typography variant="body2">
                        Latitude: {selectedPoint.location.lat.toFixed(6)}
                      </Typography>
                      <Typography variant="body2">
                        Longitude: {selectedPoint.location.lng.toFixed(6)}
                      </Typography>
                    </CardContent>
                    <CardActions>
                      <Button 
                        size="small" 
                        onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${selectedPoint.location.lat},${selectedPoint.location.lng}`, '_blank')}
                        startIcon={<MapIcon />}
                      >
                        Abrir no Google Maps
                      </Button>
                    </CardActions>
                  </Card>
                )}
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSinglePointMapOpen(false)}>Fechar</Button>
          </DialogActions>
        </Dialog>
        
        {/* Diálogo para definir o ponto de partida */}
        <Dialog
          open={startLocationDialogOpen}
          onClose={() => setStartLocationDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Definir Ponto de Partida</DialogTitle>
          <DialogContent>
            <Box sx={{ height: '50vh', width: '100%' }}>
              {/* @ts-ignore */}
              <MapComponent 
                // @ts-ignore - Property 'center' does not exist on type 'IntrinsicAttributes & MapComponentProps'
                center={startLocation}
                // @ts-ignore - Property 'zoom' does not exist on type 'IntrinsicAttributes & MapComponentProps'
                zoom={13}
                // @ts-ignore - Property 'markers' does not exist on type 'IntrinsicAttributes & MapComponentProps'
                markers={[{ position: startLocation, draggable: true }]}
                // @ts-ignore - Property 'onMarkerDragEnd' does not exist on type 'IntrinsicAttributes & MapComponentProps'
                onMarkerDragEnd={(position: any) => setStartLocation(position)}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setStartLocationDialogOpen(false)}>Cancelar</Button>
            <Button 
              onClick={() => setStartLocationDialogOpen(false)} 
              variant="contained"
            >
              Confirmar
            </Button>
          </DialogActions>
        </Dialog>

        {/* Diálogo para exibir o mapa da rota */}
        <Dialog
          open={showRouteMap}
          onClose={() => setShowRouteMap(false)}
          maxWidth="lg"
          fullWidth
          TransitionProps={{
            onExited: () => {
              // Garantir limpeza de recursos quando o diálogo for fechado
              console.log('Diálogo do mapa fechado, recursos liberados');
            }
          }}
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">Mapa da Rota</Typography>
              {optimizedRoute && (
                <Box>
                  <Chip 
                    label={`Distância: ${(optimizedRoute.totalDistance / 1000).toFixed(1)} km`}
                    color="primary"
                    size="small"
                    sx={{ mr: 1 }}
                  />
                  <Chip 
                    label={`Tempo: ${Math.round(optimizedRoute.estimatedTime / 60)} min`}
                    color="secondary"
                    size="small"
                  />
                </Box>
              )}
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ height: '70vh', width: '100%', position: 'relative' }}>
              {/* Espaço para controles adicionais do mapa */}
              
              {/* Usar chave única para garantir que o componente seja remontado quando o diálogo for aberto */}
              {showRouteMap && optimizedRoute && (
                /* @ts-ignore - Propriedades não existem no tipo IntrinsicAttributes */
                <SimpleRouteMap 
                  key={`simple-map-${Date.now()}`}
                  waypoints={optimizedRoute.waypoints}
                  center={startLocation}
                  zoom={12}
                />
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowRouteMap(false)}>Fechar</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AuthLayout>
  );
};

export default RouteOptimization;
